import { createRng } from "./utils";

export type PersonalityStatement = { id: string; text: string; dimension: string };
export type PersonalityTriad = { id: string; statements: [PersonalityStatement, PersonalityStatement, PersonalityStatement] };

const dimensions: Record<string, string[]> = {
  detail: [
    "I check important work before considering it complete.",
    "I notice small inconsistencies in information.",
    "I prefer clear standards for judging completed work.",
    "I organize tasks before beginning a demanding assignment.",
    "I keep track of unfinished details until they are resolved.",
    "I am comfortable working through a careful sequence.",
    "I review instructions when a result seems unusual.",
    "I maintain orderly records even during busy periods.",
  ],
  pace: [
    "I make decisions promptly when time is limited.",
    "I can shift quickly from one urgent task to another.",
    "I prefer a fast work pace with frequent decisions.",
    "I respond quickly when circumstances change.",
    "I can act with incomplete information when delay is costly.",
    "I remain productive when several demands arrive together.",
    "I am comfortable making repeated decisions under pressure.",
    "I recover quickly after an interruption.",
  ],
  stability: [
    "I stay composed when a situation becomes demanding.",
    "I regain focus quickly after making a mistake.",
    "Unexpected problems rarely disrupt my concentration for long.",
    "I can think clearly while others are visibly stressed.",
    "I maintain an even pace during difficult assignments.",
    "I separate immediate decisions from personal frustration.",
    "I remain patient when progress is slower than expected.",
    "I handle criticism without losing focus on the task.",
  ],
  teamwork: [
    "I share useful information before teammates need to ask.",
    "I consider how my decisions affect the rest of the team.",
    "I ask for clarification when coordination is uncertain.",
    "I support teammates during periods of high workload.",
    "I adjust my communication to the needs of the group.",
    "I contribute directly when a team problem needs attention.",
    "I keep others informed when plans change.",
    "I value reliable coordination more than personal credit.",
  ],
  assertiveness: [
    "I speak directly when a safety concern needs attention.",
    "I am willing to challenge a decision that appears unsound.",
    "I state my position clearly during disagreement.",
    "I can give firm instructions without becoming hostile.",
    "I address problems rather than hoping they resolve themselves.",
    "I am comfortable taking responsibility for a difficult decision.",
    "I raise concerns even when doing so may be unpopular.",
    "I can remain cooperative while holding a firm boundary.",
  ],
  learning: [
    "I enjoy learning why a system behaves as it does.",
    "I look for patterns when information appears confusing.",
    "I adapt my approach when evidence shows a better method.",
    "I ask questions that improve my understanding of a process.",
    "I remember lessons from unusual situations.",
    "I seek feedback that can improve future performance.",
    "I am interested in how separate parts of a system interact.",
    "I test my assumptions when new information becomes available.",
  ],
  procedure: [
    "I follow established procedures unless authorized to deviate.",
    "I treat required checks as part of the task, not an obstacle.",
    "I confirm that required steps were completed.",
    "I use standard methods during routine operations.",
    "I document exceptions when normal procedures cannot be followed.",
    "I prefer consistent rules for recurring situations.",
    "I distinguish between helpful shortcuts and prohibited omissions.",
    "I maintain standards even when supervision is limited.",
  ],
  social: [
    "I am comfortable speaking with people I do not know well.",
    "I communicate clearly in brief, fast exchanges.",
    "I can begin a necessary conversation without hesitation.",
    "I remain attentive during repeated verbal coordination.",
    "I express information confidently in group settings.",
    "I can listen carefully while preparing my response.",
    "I find frequent interaction energizing rather than distracting.",
    "I adjust quickly to different communication styles.",
  ],
};

const statements: PersonalityStatement[] = Object.entries(dimensions).flatMap(([dimension, texts]) => texts.map((text, index) => ({ id: `${dimension}-${index + 1}`, text, dimension })));

export function createPersonalityTriads(count = 108, seed = 20260613): PersonalityTriad[] {
  const rng = createRng(seed);
  const dimensionNames = Object.keys(dimensions);
  const triads: PersonalityTriad[] = [];
  const seen = new Set<string>();
  while (triads.length < count) {
    const chosenDimensions = rng.shuffle(dimensionNames).slice(0, 3);
    const selected = chosenDimensions.map((dimension) => rng.pick(statements.filter((statement) => statement.dimension === dimension))) as [PersonalityStatement, PersonalityStatement, PersonalityStatement];
    const ordered = rng.shuffle(selected) as [PersonalityStatement, PersonalityStatement, PersonalityStatement];
    const signature = ordered.map((statement) => statement.id).sort().join("|");
    if (seen.has(signature)) continue;
    seen.add(signature);
    triads.push({ id: `triad-${triads.length + 1}`, statements: ordered });
  }
  return triads;
}
